// Copyright (c) Docugami, Inc. All rights reserved.
import React, { useEffect } from 'react';
import { fetchOrganizationList, pollCreateOrganizationStatus } from '../../../store/organization/actions';
import { DgUnauthenticatedShell } from './Header';
import { useSelector } from 'react-redux';
import { AppState } from 'store';
import { useAppDispatch } from 'hooks/redux';
import { useDgAuth } from 'hooks/DgAuthentication';
import OrganizationWaitingRoom from './OrganizationWaitingRoom';
import { useOrgStatus } from 'hooks/OrgStatus';
import styled from 'styled-components';
import { setSelfServiceAccessCode } from 'utils/localStorageHelpers';
import * as queryString from 'query-string';
import { useLocation } from 'react-router-dom';
import { NavigationType, useNavigationService } from 'hooks/navigationService';
import { DgColoredButton, useThemedPrimaryButtonProps } from 'components/common/DgCommonStyledComponents';

const Root = styled.div`
    display: flex;
    justify-content: center;
    height: 100%;
    width: 100%;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    max-width: 600px;
    height: 100%;
    width: 100%;
`;

const ErrorMessage = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    padding: 40px;
    text-align: center;
`;

const ErrorTitle = styled.h1`
    font-size: 32px;
    font-weight: 600;
    color: #323130;
    margin: 0;
`;

const ErrorText = styled.p`
    font-size: 16px;
    color: #605e5c;
    margin: 0;
    line-height: 1.5;
`;

const LogoutButton = styled(DgColoredButton)`
    margin-top: 20px;
    min-width: 120px;
`;

export const CreateOrganization = (): JSX.Element => {
    const { getAccessTokenSilently } = useDgAuth();
    const { currentOrgInitializing, needToCreateAnOrg, waitlisted } = useOrgStatus();

    const organizationFetchState = useSelector((state: AppState) => state.organizations.organizationList.fetchState);
    const creationState = useSelector((state: AppState) => state.organizations.creationState);

    const dispatch = useAppDispatch();
    const location = useLocation();
    const navService = useNavigationService();
    const buttonProps = useThemedPrimaryButtonProps();

    useEffect(() => {
        const values = queryString.parse(location.search);
        if (!!values['accessCode']) {
            setSelfServiceAccessCode(values['accessCode'] as string);
        }
    }, [location.search])

    useEffect(() => {
        if (organizationFetchState.fetched === false && organizationFetchState.fetching === false) {
            dispatch(fetchOrganizationList(getAccessTokenSilently));
        }
    }, [organizationFetchState, dispatch, getAccessTokenSilently])

    // Poll org creation status while organization is being created
    useEffect(() => {
        if (creationState.created) {
            navService.navToHomePage(NavigationType.ReplaceCurrentPage);
            return;
        }

        if (creationState.creating && !!creationState.statusUrl) {
            dispatch(pollCreateOrganizationStatus(creationState.statusUrl, getAccessTokenSilently));
        }
    }, [creationState, dispatch, navService, getAccessTokenSilently])

    // We are currently fetching organizations. Nothing to render yet.
    if (!organizationFetchState.fetched) {
        return <div></div>
    }

    if (currentOrgInitializing || waitlisted) {
        return <OrganizationWaitingRoom/>
    }

    // The user does not belong to an organization. Workspace creation is now restricted to administrators.
    if (needToCreateAnOrg) {
        return (
            <DgUnauthenticatedShell>
                <Root>
                    <Content>
                        <ErrorMessage>
                            <ErrorTitle>Access Restricted</ErrorTitle>
                            <ErrorText>
                                You do not have access to any workspaces.
                            </ErrorText>
                            <ErrorText>
                                Please contact Docugami support for assistance.
                            </ErrorText>
                            <LogoutButton
                                controlButtonProps={buttonProps}
                                onClick={() => navService.logout()}
                            >
                                Log Out
                            </LogoutButton>
                        </ErrorMessage>
                    </Content>
                </Root>
            </DgUnauthenticatedShell>
        );
    }

    return (<div></div>);
}

export default CreateOrganization;
